import { useRef, useState } from 'react';
import api from 'api';
import { useApiData } from 'lib/api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { UserAttributesConditionsManager } from 'components/admin/settings/userAttributes';
import { Button } from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import {
  Checkbox,
  ColorPicker,
  Form,
  FormGroup,
  FormSection,
  ImageUploader,
  Text
} from 'components/common/form';
import { SiteSettingsThemesExport, SiteSettingsThemesImport } from '.';

import type { GetUserCustomAttributesRes } from 'shared-types/responses';

const SiteSettingsReaderThemesEdit = () => {
  const backgroundImageUploader = useRef<any>(undefined);

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { themeId },
    serverConfig
  } = useReduxState();

  const [saving, setSaving] = useState(false);

  const { data, loading, updateData } = useApiData({
    onlyIf: Boolean(themeId),
    url: `/admin/server-themes/${themeId}`
  });

  const theme = data?.theme;

  const { data: attributeData } = useApiData<GetUserCustomAttributesRes>({
    url: '/admin/user-custom-attributes'
  });

  const userCustomAttributes = attributeData?.userCustomAttributes || [];

  const availableAttributes = {};
  userCustomAttributes.forEach((availableAttribute) => {
    availableAttributes[availableAttribute.key] = availableAttribute;
  });

  const handleImport = async () => {
    await updateData({ showLoading: true });
    if (themeId === serverConfig.customThemeId) {
      dispatch(actions.updateTheme(themeId, theme.data));
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      if (backgroundImageUploader.current?.state.imageAdded) {
        const file = await backgroundImageUploader.current.upload();
        if (file) values.data.backgroundImage = file.url;
      }

      await api.put(`/admin/server-themes/${themeId}`, values);
      const response = await api.get('/admin/server-config');

      dispatch.setServerConfig(response.data.data);
      setSaving(false);

      dispatch.addNotice({
        text: messages.siteThemesUpdateSuccess,
        type: 'success'
      });
    } catch (err) {
      setSaving(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <div className="settings">
      <Box
        headerContent={
          <SiteSettingsThemesImport onImport={handleImport} themeId={themeId} />
        }
        loading={loading}
        padding={0}
        title={
          <BoxBackLink
            as="/admin/settings/reader-themes"
            href="/admin/settings/reader-themes"
            text={messages.siteThemesAll}
          />
        }
      >
        {!loading && theme ? (
          <Form
            initialValues={{
              title: theme.title,
              data: {
                sidebarDarkMode: true,
                tocChapterBorderColor: 'rgba(0, 0, 0, 0.1)',
                tocSubheadingsBorderColor: 'rgba(0, 0, 0, 0.1)',
                ...theme.data
              }
            }}
            onSubmit={handleSubmit}
          >
            {({ formState }) => (
              <div>
                <FormSection maxWidth="100%">
                  <div className="row">
                    <div className="col-1of3">
                      <FormGroup label={messages.commonTitle}>
                        <Text
                          field="title"
                          style={{ maxWidth: '100%', width: 300 }}
                        />
                      </FormGroup>
                    </div>
                  </div>
                  <FormGroup label="Attribute based reader theme">
                    <Checkbox
                      field="data.enableWithAttributeConditions.enabled"
                      id="enable-with-attribute-conditions"
                      label="Enable as a preferred reader theme with attribute conditions"
                      labelTooltip="If this is the default reader theme, the conditions will be ignored"
                    />
                    {formState.values.data?.enableWithAttributeConditions
                      ?.enabled && (
                      <UserAttributesConditionsManager
                        availableAttributes={availableAttributes}
                        fieldPrefix="data.enableWithAttributeConditions"
                        includeEmailCondition={true}
                        includeEmailDomainCondition={true}
                        style={{ marginTop: 5 }}
                      />
                    )}
                  </FormGroup>
                </FormSection>

                <FormSection maxWidth="100%" title="Background">
                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="backgroundColor">
                        <ColorPicker field="data.backgroundColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <FormGroup label="backgroundImage">
                    <ImageUploader
                      field="data.backgroundImage"
                      id="reader-theme-login-background-image"
                      maxWidth={200}
                      messages={messages}
                      setUploader={(imageUploader) =>
                        (backgroundImageUploader.current = imageUploader)
                      }
                      showPreviewImage={true}
                    />
                  </FormGroup>
                </FormSection>

                <FormSection maxWidth="100%" title="Header">
                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="headerBackgroundColor">
                        <ColorPicker field="data.headerBackgroundColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="headerColor">
                        <ColorPicker field="data.headerColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="headerColorActive">
                        <ColorPicker field="data.headerColorActive" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="headerFocusColor">
                        <ColorPicker field="data.headerFocusColor" />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Sidebar">
                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="sidebarBackgroundColor">
                        <ColorPicker field="data.sidebarBackgroundColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="sidebarHeaderBackgroundColor">
                        <ColorPicker field="data.sidebarHeaderBackgroundColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="sidebarHeaderColor">
                        <ColorPicker field="data.sidebarHeaderColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="sidebarHeaderColorActive">
                        <ColorPicker field="data.sidebarHeaderColorActive" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="sidebarColor">
                        <ColorPicker field="data.sidebarColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="sidebarColorActive">
                        <ColorPicker field="data.sidebarColorActive" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="sidebarInnerBackgroundColor">
                        <ColorPicker field="data.sidebarInnerBackgroundColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="sidebarInnerColor">
                        <ColorPicker field="data.sidebarInnerColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="sidebarInnerColorActive">
                        <ColorPicker field="data.sidebarInnerColorActive" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup>
                        <Checkbox
                          field="data.sidebarDarkMode"
                          id="sidebar-dark-mode"
                          label="sidebarDarkMode"
                        />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Table of contents">
                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="tocNumberBackgroundColor">
                        <ColorPicker field="data.tocNumberBackgroundColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="tocNumberColor">
                        <ColorPicker field="data.tocNumberColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="tocNumberBackgroundColorActive">
                        <ColorPicker field="data.tocNumberBackgroundColorActive" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="tocNumberColorActive">
                        <ColorPicker field="data.tocNumberColorActive" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="tocChapterBorderColor">
                        <ColorPicker field="data.tocChapterBorderColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="tocSubheadingsBorderColor">
                        <ColorPicker field="data.tocSubheadingsBorderColor" />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col">
                      <FormGroup label="tocBackgroundImage">
                        <Text field="data.tocBackgroundImage" />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Headings">
                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label="H1 color">
                        <ColorPicker field="data.headingOneColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="H2 color">
                        <ColorPicker field="data.headingTwoColor" />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label="H3 color">
                        <ColorPicker field="data.headingThreeColor" />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%" title="Custom fonts">
                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup label={messages.siteReaderCustomFontTitle}>
                        <Text
                          field="data.customFontTitle"
                          style={{ maxWidth: '100%', width: 300 }}
                        />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label={messages.siteReaderCustomFontValue}>
                        <Text
                          field="data.customFontValue"
                          style={{ maxWidth: '100%', width: 300 }}
                        />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup label={messages.siteReaderCustomFontUrl}>
                        <Text
                          field="data.customFontUrl"
                          style={{ maxWidth: '100%', width: 300 }}
                        />
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row-md">
                    <div className="col-1of3">
                      <FormGroup
                        label={messages.siteReaderCustomFontHeadingValue}
                      >
                        <Text
                          field="data.customFontHeadingValue"
                          style={{ maxWidth: '100%', width: 300 }}
                        />
                      </FormGroup>
                    </div>
                    <div className="col-1of3">
                      <FormGroup
                        label={messages.siteReaderCustomFontHeadingUrl}
                      >
                        <Text
                          field="data.customFontHeadingUrl"
                          style={{ maxWidth: '100%', width: 300 }}
                        />
                      </FormGroup>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%">
                  <div className="actions">
                    <div className="save">
                      <Button
                        category="success"
                        disabled={formState.invalid}
                        loading={saving}
                        text={messages.commonSaveChanges}
                        type="submit"
                      />
                    </div>
                    <div className="export">
                      <SiteSettingsThemesExport
                        value={JSON.stringify(formState.values.data)}
                      />
                    </div>
                  </div>
                </FormSection>
              </div>
            )}
          </Form>
        ) : null}
      </Box>

      <style jsx>{`
        .content {
          margin-top: 20px;
        }
        .actions {
          display: flex;
        }
        .save {
          flex-grow: 1;
        }
        .export {
          margin-right: 10px;
        }
      `}</style>
    </div>
  );
};

export default SiteSettingsReaderThemesEdit;
